import { useIsMobile } from '@hooks/useIsMobile'
import { useSetPageTitle } from '@hooks/useSetPageTitle'
import { ArrowBack } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Fade,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import clsx from 'clsx'
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styles from './page-wrapper.module.scss'
import { OvCard } from '@components'

interface IPageWrapper {
  children: React.ReactNode
  background?: 'default' | 'primary' | 'secondary' | 'primary-gradient' | 'secondary-gradient'
  beforeInner?: React.ReactNode
  pageTitle?: React.ReactNode
  loading?: boolean
  backAction?: () => void
  allowed?: boolean
  allowedMessage?: string
  actions?: React.ReactNode
  fixedActions?: boolean
}

export const PageWrapper = ({
  children,
  background = 'default',
  beforeInner,
  backAction,
  pageTitle,
  loading,
  allowed = true,
  allowedMessage,
  actions,
  fixedActions = false,
}: IPageWrapper) => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const isMobile = useIsMobile()
  const isXS = useMediaQuery((theme: any) => theme.breakpoints.down('sm'))
  const isMD = useMediaQuery((theme: any) => theme.breakpoints.between('md', 'lg'))
  const isLG = useMediaQuery((theme: any) => theme.breakpoints.up('lg'))

  const getWrapperClasses = useMemo(
    () => clsx(styles['page-wrapper'], background && styles[`page-wrapper--${background}`]),
    [background]
  )

  const getWrapperInnerClasses = useMemo(
    () =>
      clsx(
        styles['page-wrapper-inner'],
        isXS && styles['page-wrapper-inner--xs'],
        isMD && styles['page-wrapper-inner--md'],
        isLG && styles['page-wrapper-inner--lg']
      ),
    [isXS, isMD, isLG]
  )

  const handleBackAction = useCallback(() => {
    if (state?.from) return navigate(-1)

    return backAction?.()
  }, [backAction, state])

  // Extract string from pageTitle for document title
  const pageTitleString = typeof pageTitle === 'string' ? pageTitle : null
  useSetPageTitle(pageTitleString)

  return (
    <div className={getWrapperClasses}>
      {!loading && beforeInner && beforeInner}
      {/* <section className={getWrapperInnerClasses}> */}
      <Container
        maxWidth="xl"
        className={getWrapperInnerClasses}>
        {(pageTitle || backAction || actions) && (
          <Box
            className={styles['page-wrapper-title']}
            sx={{
              mb: isMobile ? 2 : 4,
            }}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent={isMobile ? undefined : 'space-between'}
              gap={isMobile ? 2 : 1}
              flex={1}>
              <Stack
                direction={'row'}
                gap={isMobile ? 1 : 2}
                flex={1}
                alignItems={'center'}>
                <Collapse
                  in={!!backAction}
                  orientation="horizontal">
                  <Fade
                    in={!!backAction}
                    timeout={400}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <IconButton
                        onClick={handleBackAction}
                        color="primary"
                        sx={{
                          padding: 0,
                        }}>
                        <ArrowBack fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </Fade>
                </Collapse>

                {loading ? (
                  <Skeleton>
                    <Box
                      className={styles['page-wrapper-title']}
                      sx={{
                        mb: isMobile ? '24px' : '32px',
                      }}>
                      <Typography variant="h1">{pageTitle}</Typography>
                    </Box>
                  </Skeleton>
                ) : (
                  <Typography
                    variant="h1"
                    color="WindowText">
                    {pageTitle}
                  </Typography>
                )}
              </Stack>
              {allowed && actions && (
                <Stack
                  direction={'row'}
                  gap={1}
                  justifyContent={isMobile ? 'space-between' : 'initial'}
                  alignItems={isMobile ? 'stretch' : 'center'}>
                  {actions}
                </Stack>
              )}
            </Stack>
          </Box>
        )}
        {loading ? (
          <Fade in={loading}>
            <Box
              sx={{
                display: 'flex',
                flex: '1 0 auto',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <CircularProgress sx={{ height: '100%' }} />
            </Box>
          </Fade>
        ) : (
          <>
            {allowed ? (
              children
            ) : (
              <Typography>{allowedMessage ?? 'Het bezoeken van deze pagina is (nog) niet toegestaan'}</Typography>
            )}
          </>
        )}
      </Container>
      {/* </section> */}
      {actions && fixedActions && (
        <OvCard
          elevation={3}
          sx={{
            position: 'fixed',
            left: 0,
            bottom: isMobile ? 56 : 0,
            right: 0,
            borderRadius: 0,
            zIndex: 98,
            p: 2,
            transition: 'bottom 0.3s ease',
          }}>
          <Container
            maxWidth="xl"
            disableGutters>
            <Stack
              display="flex"
              direction="row"
              justifyContent={'flex-end'}
              gap={1}>
              {actions}
            </Stack>
          </Container>
        </OvCard>
      )}
    </div>
  )
}
